import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails
} from 'amazon-cognito-identity-js';
import { useAuth } from './contexts/AuthContext';
import { API_ENDPOINTS } from './constants';
import { Header, Card, Button, Input } from './components/ui';
import { tokens } from './styles/tokens';

// Debug environment variables
console.log('VerifyEmailPage Environment variables:', {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
  Region: process.env.REACT_APP_AWS_REGION
});

// Cognito pool setup
const poolData = {
  UserPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID,
  ClientId: process.env.REACT_APP_COGNITO_USER_POOL_CLIENT_ID,
};

console.log('VerifyEmailPage Pool data:', poolData);

function VerifyEmailPage() {
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loginAfterVerification, setLoginAfterVerification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  const email = location.state?.email || '';
  const message = location.state?.message || '';
  
  // Create user pool instance only if we have the required values
  let userPool = null;
  if (poolData.UserPoolId && poolData.ClientId) {
    userPool = new CognitoUserPool(poolData);
  } else {
    console.error('VerifyEmailPage - Missing required Cognito configuration:', poolData);
  }

  // Redirect if no email provided
  useEffect(() => {
    if (!email) {
      navigate('/signup');
    }
  }, [email, navigate]);

  // Save user to backend database
  async function saveUserToBackend(cognitoUser, userAttributes) {
    try {
      console.log('Starting to save user to backend...');
      
      // Get current session
      cognitoUser.getSession(async (err, session) => {
        if (err) {
          console.error('Error getting session:', err);
          return;
        }

        if (!session || !session.isValid()) {
          console.error('Invalid session');
          return;
        }

        const idToken = session.getIdToken().getJwtToken();
        console.log('Got ID token for backend save');

        const userData = {
          email: userAttributes.email,
          firstName: userAttributes.given_name || '',
          lastName: userAttributes.family_name || '',
          walletAddress: userAttributes['custom:wallet_address'] || '',
          dateOfBirth: userAttributes.birthdate || '',
          cognitoId: userAttributes.sub,
          verified: true
        };

        console.log('Sending user data to backend:', userData);

        const response = await fetch(`${API_ENDPOINTS.USER_API}/user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`,
          },
          body: JSON.stringify(userData),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Backend save failed:', response.status, errorText);
        } else {
          const result = await response.json();
          console.log('User saved to backend successfully:', result);
        }
      });
    } catch (error) {
      console.error('Error saving user to backend:', error);
    }
  }

  const handleVerification = async (e) => {
    e.preventDefault();
    
    if (!verificationCode) {
      setErrors({ code: 'Verification code is required' });
      return;
    }

    if (!userPool) {
      setErrors({ general: 'Configuration error. Please try again later.' });
      return;
    }
    
    setLoading(true);
    setErrors({});
    
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });
    
    cognitoUser.confirmRegistration(verificationCode, true, async (err, result) => {
      if (err) {
        console.error('Verification error:', err);
        setLoading(false);
        if (err.code === 'CodeMismatchException') {
          setErrors({ code: 'Invalid verification code. Please check and try again.' });
        } else if (err.code === 'ExpiredCodeException') {
          setErrors({ code: 'Verification code has expired. Please request a new one.' });
        } else {
          setErrors({ general: err.message || 'Verification failed' });
        }
        return;
      }
      
      console.log('Verification successful:', result);
      setSuccessMessage('Email verified successfully!');
      setLoginAfterVerification(true);
      setLoading(false);
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!password) {
      setErrors({ password: 'Password is required' });
      return;
    }

    if (!userPool) {
      setErrors({ general: 'Configuration error. Please try again later.' });
      return;
    }
    
    setLoading(true);
    setErrors({});
    
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password
    });
    
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });
    
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: async (session) => {
        console.log('Login successful:', session);
        
        // Get user attributes for backend save
        cognitoUser.getUserAttributes(async (err, attributes) => {
          if (err) {
            console.error('Error getting user attributes:', err);
          } else {
            const userAttributes = {};
            attributes.forEach(attr => {
              userAttributes[attr.Name] = attr.Value;
            });
            console.log('User attributes:', userAttributes);
            
            // Save to backend
            await saveUserToBackend(cognitoUser, userAttributes);
          }
        });
        
        // Navigate to home with success message
        navigate('/home', { 
          state: { 
            successMessage: 'Welcome to ELXR Sportsbook! Your account is ready.',
            fromVerification: true
          } 
        });
      },
      onFailure: (err) => {
        console.error('Login error:', err);
        setLoading(false);
        if (err.code === 'NotAuthorizedException') {
          setErrors({ password: 'Incorrect password' });
        } else if (err.code === 'UserNotFoundException') {
          setErrors({ general: 'User not found' });
        } else {
          setErrors({ general: err.message || 'Login failed' });
        }
      }
    });
  };

  const handleResendCode = () => {
    if (!userPool) {
      setErrors({ general: 'Configuration error. Please try again later.' });
      return;
    }

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });
    
    cognitoUser.resendConfirmationCode((err, result) => {
      if (err) {
        console.error('Resend code error:', err);
        setErrors({ general: err.message || 'Failed to resend code' });
      } else {
        console.log('Code resent:', result);
        setSuccessMessage('Verification code resent! Check your email.');
        setErrors({});
      }
    });
  };

  const menuItems = [
    { label: 'Home', href: '/home' },
    { label: 'Sports', href: '/sports' },
    { label: 'My Bets', href: '/my-bets' }
  ];

  return (
    <div style={{ minHeight: '100vh', background: tokens.colors.dark[900] }}>
      <Header logo="ELXR Sportsbook" menuItems={menuItems} />
      
      <section style={{
        background: `linear-gradient(135deg, ${tokens.colors.dark[800]} 0%, ${tokens.colors.dark[900]} 100%)`,
        minHeight: 'calc(100vh - 4rem)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${tokens.spacing[8]} 0`,
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `radial-gradient(circle at 20% 80%, ${tokens.colors.primary[900]}22 0%, transparent 50%),
                           radial-gradient(circle at 80% 20%, ${tokens.colors.secondary[900]}22 0%, transparent 50%)`,
          opacity: 0.5
        }} />
        
        <div className="container" style={{ 
          maxWidth: '500px',
          width: '100%',
          position: 'relative',
          zIndex: 1
        }}>
          <Card variant="elevated" padding="large">
            <Card.Body>
              <h1 style={{ 
                fontSize: tokens.typography.fontSize['3xl'],
                fontWeight: tokens.typography.fontWeight.bold,
                background: tokens.colors.gradients.glow,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: tokens.spacing[2],
                textAlign: 'center'
              }}>
                {loginAfterVerification ? 'Complete Registration' : 'Verify Your Email'}
              </h1>
              
              {!loginAfterVerification && (
                <>
                  <p style={{ 
                    textAlign: 'center',
                    marginBottom: tokens.spacing[6],
                    color: tokens.colors.neutral[400]
                  }}>
                    We've sent a verification code to {email}
                  </p>
                  
                  {message && (
                    <div style={{
                      background: `${tokens.colors.primary[900]}33`,
                      border: `1px solid ${tokens.colors.primary[700]}`,
                      borderRadius: tokens.borderRadius.md,
                      padding: tokens.spacing[3],
                      marginBottom: tokens.spacing[6],
                      color: tokens.colors.primary[400],
                      fontSize: tokens.typography.fontSize.sm,
                      textAlign: 'center'
                    }}>
                      {message}
                    </div>
                  )}
                </>
              )}
              
              {successMessage && (
                <div style={{
                  background: `${tokens.colors.accent[900]}33`,
                  border: `1px solid ${tokens.colors.accent[700]}`,
                  borderRadius: tokens.borderRadius.md,
                  padding: tokens.spacing[3],
                  marginBottom: tokens.spacing[6],
                  color: tokens.colors.accent[400],
                  fontSize: tokens.typography.fontSize.sm,
                  textAlign: 'center'
                }}>
                  {successMessage}
                </div>
              )}
              
              {errors.general && (
                <div style={{
                  background: `${tokens.colors.error[900]}33`,
                  border: `1px solid ${tokens.colors.error[700]}`,
                  borderRadius: tokens.borderRadius.md,
                  padding: tokens.spacing[3],
                  marginBottom: tokens.spacing[6],
                  color: tokens.colors.error[400],
                  fontSize: tokens.typography.fontSize.sm
                }}>
                  {errors.general}
                </div>
              )}
              
              {!loginAfterVerification ? (
                <form onSubmit={handleVerification}>
                  <Input
                    type="text"
                    name="code"
                    label="Verification Code"
                    placeholder="Enter 6-digit code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    error={errors.code}
                    disabled={loading}
                    required
                    fullWidth
                    autoComplete="one-time-code"
                  />
                  
                  <Button
                    type="submit"
                    variant="primary"
                    size="large"
                    fullWidth
                    loading={loading}
                    disabled={loading}
                    style={{ marginTop: tokens.spacing[6] }}
                  >
                    Verify Email
                  </Button>
                  
                  <div style={{ 
                    textAlign: 'center',
                    marginTop: tokens.spacing[6]
                  }}>
                    <button
                      type="button"
                      onClick={handleResendCode}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: tokens.colors.primary[400],
                        cursor: 'pointer',
                        fontSize: tokens.typography.fontSize.sm,
                        textDecoration: 'underline'
                      }}
                    >
                      Resend verification code
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleLogin}>
                  <p style={{ 
                    textAlign: 'center',
                    marginBottom: tokens.spacing[6],
                    color: tokens.colors.neutral[400]
                  }}>
                    Enter your password to complete registration
                  </p>
                  
                  <Input
                    type="password"
                    name="password"
                    label="Password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={errors.password}
                    disabled={loading}
                    required
                    fullWidth
                    autoComplete="current-password"
                  />
                  
                  <Button
                    type="submit"
                    variant="success"
                    size="large"
                    fullWidth
                    loading={loading}
                    disabled={loading}
                    style={{ marginTop: tokens.spacing[6] }}
                  >
                    Complete Registration
                  </Button>
                </form>
              )}
              
              <p style={{ 
                textAlign: 'center',
                marginTop: tokens.spacing[6],
                color: tokens.colors.neutral[400]
              }}>
                Need help?{' '}
                <Link 
                  to="/contact"
                  style={{
                    color: tokens.colors.primary[400],
                    fontWeight: tokens.typography.fontWeight.medium,
                    textDecoration: 'none'
                  }}
                >
                  Contact Support
                </Link>
              </p>
            </Card.Body>
          </Card>
        </div>
      </section>
    </div>
  );
}

export default VerifyEmailPage; 